/**
 * Services for interacting with license management endpoints
 */

import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import {
  LICENSED_PRODUCTS_QUERY_KEY,
  useGetLicensedProduct,
} from "src/helpers/rbac-rules";

// Types for license info
export interface LicenseInfo {
  status: string;
  type?: string;
  licensed_to?: string;
  issue_date?: string;
  expiry_date?: string;
  products: string[];
  days_until_expiry?: number;
}

export interface LicenseUploadResponse {
  message: string;
  license_info?: LicenseInfo;
}

/**
 * Fetch license information
 */
const getLicenseInfo = async (): Promise<LicenseInfo> => {
  const response = await axios.get<LicenseInfo>(
    `${nuclearn_api_url}/auth/license-info`,
  );
  return response.data;
};

/**
 * Hook to get license information
 */
export const useGetLicenseInfo = () => {
  return useQuery<LicenseInfo, Error>("licenseInfo", getLicenseInfo, {
    refetchOnWindowFocus: false,
    retry: false, // Don't retry on 404 (no license)
    staleTime: 60000, // Cache for 1 minute
  });
};

/**
 * Upload a new license key file
 */
const uploadLicenseKey = async (file: File): Promise<LicenseUploadResponse> => {
  const formData = new FormData();
  formData.append("license_file", file);

  const response = await axios.post<LicenseUploadResponse>(
    `${nuclearn_api_url}/auth/license-upload`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

/**
 * Hook to get licensed products. Same query (and key) as the role and rollout
 * gates, so a license upload refreshes them together.
 */
export const useGetLicensedProducts = useGetLicensedProduct;

/**
 * Hook to upload license key
 */
export const useUploadLicenseKey = () => {
  const queryClient = useQueryClient();

  return useMutation<LicenseUploadResponse, Error, File>(uploadLicenseKey, {
    onSuccess: () => {
      // Invalidate and refetch license info after successful upload
      queryClient.invalidateQueries("licenseInfo");
      queryClient.invalidateQueries(LICENSED_PRODUCTS_QUERY_KEY);
    },
  });
};
